import { useState } from 'react';
import { walletApi, CreatePixKeyPayload } from '@/api/wallet-api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Copy, Check, Trash2, Key, Plus, AlertCircle } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks';

const PIX_KEY_TYPES = [
  { value: 'CPF', label: 'CPF' },
  { value: 'CNPJ', label: 'CNPJ' },
  { value: 'EMAIL', label: 'E-mail' },
  { value: 'PHONE', label: 'Telefone' },
  { value: 'EVP', label: 'Chave Aleatória (EVP)' },
] as const;

export function PixKeysPage() {
  const location = useLocation();
  const { user } = useAuth();
  const backPath = location.pathname.startsWith('/medico') || user?.role === 'DOCTOR' ? '/medico/carteira' : '/carteira';

  const queryClient = useQueryClient();

  const { data: pixKey, isLoading } = useQuery({
    queryKey: ['pixKeys'],
    queryFn: () => walletApi.getPixKey(),
  });

  const [keyType, setKeyType] = useState<CreatePixKeyPayload['type']>('CPF');
  const [keyValue, setKeyValue] = useState('');
  const [copied, setCopied] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const createMutation = useMutation({
    mutationFn: (payload: CreatePixKeyPayload) => walletApi.createPixKey(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pixKeys'] });
      setKeyValue('');
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { type: CreatePixKeyPayload['type']; key: string }) =>
      walletApi.updatePixKey(pixKey!.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pixKeys'] });
      setKeyValue('');
      setShowForm(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => walletApi.deletePixKey(pixKey!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pixKeys'] });
    },
  });

  const copyToClipboard = () => {
    if (pixKey?.key) {
      navigator.clipboard.writeText(pixKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="h-8 w-48 animate-pulse bg-muted rounded" />
        <div className="h-48 animate-pulse bg-muted rounded-xl" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <Link
          to={backPath}
          className="text-sm text-primary-600 hover:text-primary-700 font-medium mb-2 inline-block"
        >
          &larr; Voltar para Carteira
        </Link>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Key className="w-6 h-6" />
            Chaves PIX
          </h1>
        </div>
      </div>

      {pixKey ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-border shadow-sm overflow-hidden">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold">Chave PIX Cadastrada</h2>
              <span className={`text-xs px-2 py-1 rounded-full ${pixKey.verifiedAt ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'}`}>
                {pixKey.verifiedAt ? 'Verificada' : 'Pendente'}
              </span>
            </div>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              <div>
                <p className="text-sm text-muted-foreground">Tipo</p>
                <p className="font-medium">{PIX_KEY_TYPES.find(t => t.value === pixKey.type)?.label}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Chave</p>
                <p className="font-mono font-medium">{pixKey.maskedKey}</p>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Cadastrada em {formatDate(pixKey.createdAt)}</span>
              {pixKey.verifiedAt && <span>Verificada em {formatDate(pixKey.verifiedAt)}</span>}
            </div>

            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={copyToClipboard} className="flex-1">
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? 'Copiado!' : 'Copiar Chave'}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowForm(true)}>
                Alterar
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-border shadow-sm">
          <div className="py-12 text-center">
            <Key className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">Nenhuma chave PIX cadastrada</h3>
            <p className="text-sm text-muted-foreground mb-6">
              Cadastre uma chave PIX para receber transferências e saques.
            </p>
            {!showForm && (
              <Button onClick={() => setShowForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Cadastrar Chave PIX
              </Button>
            )}
          </div>
        </div>
      )}

      {showForm && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-border shadow-sm overflow-hidden">
          <div className="p-6 border-b border-border">
            <h2 className="font-semibold">{pixKey ? 'Alterar Chave PIX' : 'Cadastrar Chave PIX'}</h2>
          </div>
          <div className="p-6 space-y-4">
            {(createMutation.error || updateMutation.error) && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>Erro ao salvar chave PIX. Verifique os dados e tente novamente.</span>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Tipo de Chave</label>
              <select
                value={keyType}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setKeyType(e.target.value as CreatePixKeyPayload['type'])}
                className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {PIX_KEY_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Chave PIX</label>
              <input
                type={keyType === 'EMAIL' ? 'email' : 'text'}
                value={keyValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKeyValue(e.target.value)}
                placeholder={
                  keyType === 'CPF' ? '000.000.000-00' :
                  keyType === 'CNPJ' ? '00.000.000/0000-00' :
                  keyType === 'EMAIL' ? 'seu@email.com' :
                  keyType === 'PHONE' ? '+55 (00) 00000-0000' :
                  'Chave aleatória (UUID)'
                }
                className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <p className="text-xs text-muted-foreground">
                {keyType === 'CPF' && 'Digite um CPF válido (11 dígitos)'}
                {keyType === 'CNPJ' && 'Digite um CNPJ válido (14 dígitos)'}
                {keyType === 'EMAIL' && 'Digite um endereço de e-mail válido'}
                {keyType === 'PHONE' && 'Digite um telefone com código do país (55) + DDD + número'}
                {keyType === 'EVP' && 'Chave única gerada automaticamente pelo sistema'}
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => {
                  const payload = { type: keyType, key: keyValue };
                  if (pixKey) {
                    updateMutation.mutate(payload);
                  } else {
                    createMutation.mutate(payload);
                  }
                }}
                disabled={createMutation.isPending || updateMutation.isPending || !keyValue}
              >
                {(createMutation.isPending || updateMutation.isPending) ? 'Salvando...' : 'Salvar'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowForm(false);
                  setKeyValue('');
                }}
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
